import { useEffect, useRef, useState } from 'react'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import Dropdown from '@jimmyboys/liita-ui/dist/components/Dropdown'
import TextInput from '@jimmyboys/liita-ui/dist/components/TextInput'
import { useFetch } from 'store'

import styles from '../TransportDetailDialog/TransportDetailDialog.module.sass'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import { Options } from 'models'
import Datepicker from 'components/Datepicker/Datepicker'
import moment from 'moment'
import Checkbox from '@jimmyboys/liita-ui/dist/components/Checkbox'
import Skeleton from '@jimmyboys/liita-ui/dist/components/Skeleton'
import { RequisitionTypeValue } from 'models/enums/RequisitionTypeValue'
import {
	Controller,
	FormProvider,
	useFieldArray,
	useForm,
	useFormContext,
	useWatch,
} from 'react-hook-form'
import { insertTransportSchema } from './InsertTransportSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { useReload } from 'store'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'
import useSession from 'hooks/useSession'
import AlertBox from 'components/AlertBox/AlertBox'

const Field = ({ label, required, children, error }) => (
	<div className={styles['field-h']}>
		<span className={styles['field-label-h']}>
			{label}
			{required ? '*' : ''}
		</span>

		<div className={styles['field-control']}>
			{children}
			{error && <small className={styles['error']}>{error}</small>}
		</div>
	</div>
)

const TransportRow = ({
	act,
	index,
	editing,
	setEditing,
	transportOptions,
	getDateTimeErrorMessage,
	setFieldErrors,
	setErrorMessage,
	selected,
	fillTransportFieldsForPhysiotherapy,
	i18n,
	styles,
}) => {
	const { control, setValue, getValues } = useFormContext()

	const row = useWatch({
		control,
		name: `transportList.${index}`,
	})

	const isEditing = editing.rowId === index && editing.editing
	const isSelected = row?.selected ?? false
	const toggleSelection = () => {
		if (isEditing) return

		setValue(`transportList.${index}.selected`, !isSelected)
	}

	const handleSaveRow = () => {
		const row = getValues(`transportList.${index}`)
		const dateError = getDateTimeErrorMessage(row)

		if (
			!row.date ||
			!row.time ||
			!row.transportId ||
			!row.transportName ||
			dateError
		) {
			setErrorMessage(
				dateError || i18n`Please fill in all required fields`
			)
			setFieldErrors(true)
			return
		}

		setEditing({ rowId: null, editing: false })

		if (
			Number(selected?.requisitionTypeId) ===
				RequisitionTypeValue.PHYSIOTHERAPY &&
			index === 0
		) {
			fillTransportFieldsForPhysiotherapy()
		}
	}

	return (
		<tr
			className={[
				styles['transport-row'],
				isSelected ? styles['selected'] : '',
				isEditing ? styles['is-editing'] : '',
			]
				.filter(Boolean)
				.join(' ')}
			onClick={toggleSelection}
			aria-selected={isSelected && !isEditing}
		>
			<td>
				{!isEditing && (
					<Checkbox
						className={styles['row-checkbox']}
						checked={isSelected}
						disabled={isEditing}
						onChange={toggleSelection}
					/>
				)}
			</td>
			<td>{act.description}</td>

			<td>
				{isEditing ? (
					<Controller
						name={`transportList.${index}.date`}
						control={control}
						render={({ field }) => (
							<Datepicker
								type='date'
								text={i18n`Date`}
								initialDate={moment(
									field.value,
									'DD/MM/YYYY',
									true
								)}
								onChange={(date) =>
									field.onChange(date.format('DD/MM/YYYY'))
								}
							/>
						)}
					/>
				) : (
					row?.date
				)}
			</td>

			<td>
				{isEditing ? (
					<Controller
						name={`transportList.${index}.time`}
						control={control}
						render={({ field }) => (
							<TextInput
								className={styles['control']}
								type='time'
								text={i18n`Time`}
								value={field.value}
								onChange={(e) => field.onChange(e.target.value)}
							/>
						)}
					/>
				) : (
					row?.time
				)}
			</td>

			<td>
				{isEditing ? (
					<Controller
						name={`transportList.${index}.transportId`}
						control={control}
						render={({ field }) => (
							<Dropdown
								className={styles['control']}
								optionsAlign='left'
								displayValue={false}
								placeholder={i18n`Select transport type`}
								value={String(field.value ?? '')}
								options={transportOptions.map((o) => ({
									...o,
									value: String(o.value),
								}))}
								onChange={(e) => {
									const newValue = String(e.target.value)
									field.onChange(newValue)

									setValue(
										`transportList.${index}.transportName`,
										transportOptions.find(
											(o) => String(o.value) === newValue
										)?.text ?? ''
									)
								}}
							/>
						)}
					/>
				) : (
					row?.transportName
				)}
			</td>

			<td
				onClick={(e) => e.stopPropagation()}
				className={styles['row-actions']}
			>
				<ActionButton
					className='icon-container'
					type='button'
					onClick={() =>
						isEditing
							? handleSaveRow()
							: setEditing({ rowId: index, editing: true })
					}
				>
					<Icon
						name={isEditing ? 'success' : 'edit-pencil'}
						height='20px'
						width='20px'
					/>
				</ActionButton>
				{/* {!isEditing && (
					<Checkbox
						className={styles['row-checkbox']}
						checked={isSelected}
						disabled={isEditing}
						onChange={toggleSelection}
					/>
				)} */}
			</td>
		</tr>
	)
}

const InsertTransportDialog = (props) => {
	const { resolve, dismiss, dialogId } = props
	const { i18n, pushNotification } = useGlobal()
	const processId = props.data?.processId
	const reload = useReload()
	const { logout } = useSession()

	const [editing, setEditing] = useState({
		rowId: null,
		editing: false,
	})
	const [successMessage, setSuccessMessage] = useState(null)
	const [fieldErrors, setFieldErrors] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)
	const warningRef = useRef(null)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(insertTransportSchema),
		// shouldUnregister: false,
		defaultValues: {
			requisitionId: '',
			establishment: '',
			useAlternativeAddress: false,
			alternativeAddress: '',
			observations: '',
			transportList: [],
		},
	})

	const {
		control,
		handleSubmit,
		formState,
		setValue,
		watch,
		getValues,
		setError: setFormError,
	} = methods

	const { fields, update } = useFieldArray({
		control,
		name: 'transportList',
	})

	const useAlternativeAddress = watch('useAlternativeAddress')
	const requisitionId = watch('requisitionId')
	const transportList = watch('transportList')

	// Fontes: consultas futuras do processo elegíveis para transporte.
	const { state: sourcesState, contents: sourcesContents } = useFetch(
		processId
			? `/clinical_processes/${processId}/transports/sources`
			: null,
		null
	)
	const sources =
		sourcesState === 'hasValue' && Array.isArray(sourcesContents)
			? sourcesContents
			: []

	// Auto-preenchimento: chamado ao escolher uma consulta.
	const { state: prefillState, contents: prefillContents } = useFetch(
		requisitionId
			? `/clinical_processes/${processId}/transports/prefill/${requisitionId}`
			: null,
		null
	)
	const selected =
		prefillState === 'hasValue' && prefillContents ? prefillContents : null

	const { state: transportState, contents: transportContents } = useFetch(
		'/metadata/transport_types',
		(json) =>
			Options.fromSimpleObjectJSON({
				id: `${json.id ?? json.code ?? json.value}`,
				name: i18n.translate(
					json.name ?? json.text ?? json.opinionType
				),
			})
	)

	const transportOptions =
		transportState === 'hasValue' && Array.isArray(transportContents)
			? transportContents.filter(
					(tr) => !['1', '2'].includes(String(tr.value))
			  )
			: []

	useEffect(() => {
		if (prefillState === 'hasValue' && prefillContents) {
			setValue('establishment', prefillContents.establishment ?? '', {
				shouldValidate: true,
			})

			setValue(
				'transportList',
				(prefillContents.medicalActRequisitions ?? []).map(
					(act, index) => ({
						id: index,
						medicalActId: act.medicalActId,
						requisitionId: act.requisitionId,
						date: act.transportDate,
						time: act.transportTime,
						transportId: act.transportType
							? String(act.transportType.id)
							: '',
						transportName: act.transportType?.transportType ?? '',
						description: act.medicalActDescription,
						requisitionActId: act.requisitionActId,
					})
				),
				{ shouldValidate: true }
			)
		}
	}, [prefillState, prefillContents, setValue])

	useEffect(() => {
		if (fieldErrors) {
			warningRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}
	}, [fieldErrors])

	const objectOptions = sources.map((o) => ({
		value: o.requisitionId,
		text: o.requisitionType,
	}))

	/* Helpers */

	const getDateTimeErrorMessage = (item) => {
		const today = moment().startOf('day')
		const selectedDate = moment(item.date, 'DD/MM/YYYY', true)

		if (!selectedDate.isValid()) {
			return i18n`Invalid date format`
		}

		if (!item.time) {
			return i18n`Time is required`
		}

		if (selectedDate.isBefore(today)) {
			return i18n`Date cannot be in the past`
		}

		if (selectedDate.isSame(today)) {
			const selectedDateTime = moment(
				`${item.date} ${item.time}`,
				'DD/MM/YYYY HH:mm',
				true
			)

			if (!selectedDateTime.isValid()) {
				return i18n`Invalid time format`
			}

			if (!selectedDateTime.isAfter(moment())) {
				return i18n`Time must be in the future`
			}
		}

		return null
	}

	const getNextBusinessDay = (date) => {
		const nextDay = moment(date, 'DD/MM/YYYY', true).clone().add(1, 'day')

		// 0 = Sunday, 6 = Saturday
		while (nextDay.day() === 0 || nextDay.day() === 6) {
			nextDay.add(1, 'day')
		}

		return nextDay.format('DD/MM/YYYY')
	}

	const handleClose = () => {
		pushNotification(
			undefined,
			'success',
			i18n`Transport requested successfully!`
		)
		resolve && resolve()
		return
	}

	const onSubmit = (formData) => {
		if (submitting) return

		const rowsToSend = formData.transportList
			.filter((row) => row.selected)
			.map(({ selected, ...rest }) => rest)

		if (rowsToSend.length === 0) {
			setErrorMessage(
				i18n`transport list is empty. Please select at least one transport.`
			)
			setFieldErrors(true)
			return
		}

		if (
			selected?.establishmentId === null &&
			(!formData.useAlternativeAddress ||
				!formData.alternativeAddress?.trim())
		) {
			const msg = i18n`Since the source requisition is outside the network, an alternative address is required.`

			// marca o campo que está visível ao utilizador
			setFormError(
				formData.useAlternativeAddress
					? 'alternativeAddress'
					: 'useAlternativeAddress',
				{ type: 'manual', message: msg }
			)
			// setErrorMessage(msg)
			setFieldErrors(true)
			return
		}

		if (
			selected?.establishmentId === null &&
			formData.alternativeAddress?.trim().length < 5
		) {
			const msg = i18n`The alternative address must be at least 5 characters long.`

			setFormError('alternativeAddress', { type: 'manual', message: msg })
			return
		}

		setFieldErrors(false)
		setErrorMessage('')

		const endpoint = `${API_URL}/api/clinical_processes/${processId}/transport`

		setError(null)
		setSubmitting('submitting')

		return fetch(
			endpoint,
			fetchOptions({
				method: 'POST',
				body: { ...formData, transportList: rowsToSend },
			})
		)
			.then((r) => parseJSONContent(r))
			.then((r) => {
				// resolve && resolve()
				reload([`/clinical_processes/${processId}/transport`])
				// pushNotification(
				// 	undefined,
				// 	'success',
				// 	i18n`Transport requested successfully!`
				// 	// 'test'
				// )

				setSubmitting(false)
				setSuccessMessage(
					r?.Info ??
						r?.Message ??
						i18n`Transport requested successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	const fillTransportFieldsForPhysiotherapy = () => {
		const list = getValues('transportList') || []
		if (!list.length) return

		const firstRow = list[0]
		let lastDate = moment(firstRow.date, 'DD/MM/YYYY', true)

		const nextList = list.map((item, index) => {
			if (index === 0) return item

			lastDate = getNextBusinessDay(lastDate)

			return {
				...item,
				transportId: firstRow.transportId,
				transportName:
					transportOptions.find(
						(o) => String(o.value) === String(firstRow.transportId)
					)?.text ?? '',
				date: lastDate,
				time: firstRow.time,
			}
		})

		setValue('transportList', nextList, {
			shouldValidate: true,
			shouldDirty: true,
		})
	}

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			{successMessage && (
				<div className={`${styles['result']} ${styles['reveal']}`}>
					<span className={styles['result-icon']}>
						<Icon name='success' width='30px' height='30px' />
					</span>

					<h4 className={styles['result-title']}>
						{i18n`Transport requested successfully`}
					</h4>

					<p className={styles['result-message']}>{successMessage}</p>
				</div>
			)}
			{!successMessage && (
				<FormProvider {...methods}>
					<form
						id={'form_insert_transport'}
						className={`${styles['root']}`}
						onSubmit={handleSubmit(onSubmit)}
						disabled={submitting}
					>
						<div className={styles['card']}>
							<h4
								className={styles['section-title']}
							>{i18n`Transport data`}</h4>

							<Controller
								name='requisitionId'
								control={control}
								render={({ field, fieldState }) => (
									<Field
										label={i18n`Transport object`}
										required
										error={fieldState.error?.message}
									>
										<Dropdown
											className={styles['control']}
											optionsAlign='left'
											displayValue={false}
											placeholder={i18n`Select transport object`}
											value={field.value}
											options={objectOptions}
											onChange={(e) =>
												field.onChange(e.target.value)
											}
										/>
									</Field>
								)}
							/>

							{selected?.transportRequisitionAlreadyExists && (
								<p
									className={`${styles['warning']} ${styles['reveal']}`}
								>
									{i18n`There is already a transport request associated with this requisition`}
								</p>
							)}

							<Controller
								name='establishment'
								control={control}
								render={({ field }) => (
									<Field label={i18n`Transport location`}>
										<TextInput
											className={styles['control']}
											value={field.value}
											readOnly
										/>
									</Field>
								)}
							/>

							<Controller
								name='useAlternativeAddress'
								control={control}
								render={({ field }) => (
									<Field
										label={i18n`Provider alternative address`}
									>
										<label
											className={styles['checkbox-field']}
										>
											<Checkbox
												className={styles['checkbox']}
												checked={field.value}
												onChange={() =>
													field.onChange(!field.value)
												}
											/>
										</label>
									</Field>
								)}
							/>

							{useAlternativeAddress && (
								<Controller
									name='alternativeAddress'
									control={control}
									render={({ field, fieldState }) => (
										<Field
											label={i18n`Address`}
											error={fieldState.error?.message}
										>
											<TextInput
												className={styles['control']}
												value={field.value}
												onChange={(e) =>
													field.onChange(
														e.target.value
													)
												}
												placeholder={i18n`Alternative address`}
												type='text'
												multiline
												noResize
											/>
										</Field>
									)}
								/>
							)}

							<Controller
								name='observations'
								control={control}
								render={({ field, fieldState }) => (
									<Field
										label={i18n`Observations`}
										error={fieldState.error?.message}
									>
										<TextInput
											className={styles['control']}
											multiline
											rows={3}
											value={field.value}
											onChange={(e) =>
												field.onChange(e.target.value)
											}
											placeholder={i18n`Observations`}
										/>
									</Field>
								)}
							/>
						</div>

						{prefillState.toLowerCase() === 'loading' &&
							loadSkeleton()}

						{/* Lista de Transporte (preenchida ao escolher o objeto) */}
						{transportList?.length > 0 && (
							<div
								className={`${styles['card']} ${styles['reveal']}`}
							>
								<h4
									className={styles['section-title']}
								>{i18n`Transport List`}</h4>
								{fieldErrors && (
									<p
										className={`${styles['warning']} ${styles['reveal']}`}
										ref={warningRef}
									>
										{errorMessage ||
											i18n`Please fill in all required fields in the transport list.`}
									</p>
								)}
								<table className={styles['table']}>
									<thead>
										<tr>
											<th></th>
											<th>{i18n`Medical Act`}</th>
											<th>{i18n`Date`}</th>
											<th>{i18n`Hour`}</th>
											<th>{i18n`Type`}</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{fields.map((act, index) => (
											<TransportRow
												key={act.id}
												act={act}
												index={index}
												editing={editing}
												setEditing={setEditing}
												transportOptions={
													transportOptions
												}
												getDateTimeErrorMessage={
													getDateTimeErrorMessage
												}
												setFieldErrors={setFieldErrors}
												setErrorMessage={
													setErrorMessage
												}
												selected={selected}
												fillTransportFieldsForPhysiotherapy={
													fillTransportFieldsForPhysiotherapy
												}
												i18n={i18n}
												styles={styles}
											/>
										))}
									</tbody>
								</table>
							</div>
						)}
					</form>
				</FormProvider>
			)}

			{/* Ações */}
			<menu className={styles['footer']}>
				{successMessage ? (
					<ActionButton primary type='button' onClick={handleClose}>
						<small>
							<strong>{i18n`Close`}</strong>
						</small>
					</ActionButton>
				) : (
					<>
						<ActionButton type='button' secondary onClick={dismiss}>
							<small>
								<strong>{i18n`Cancel`}</strong>
							</small>
						</ActionButton>
						<ActionButton primary form='form_insert_transport'>
							<small>
								<strong>
									{submitting ? i18n`Submitting` : i18n`Send`}
								</strong>
							</small>
						</ActionButton>
					</>
				)}
			</menu>
		</>
	)
}

export default InsertTransportDialog

const loadSkeleton = () => {
	return (
		<div className={`${styles['card']} ${styles['reveal']}`}>
			<h4 className={styles['section-title']}>
				<Skeleton
					className={`${styles['skeleton']} ${styles['small']}`}
				/>
			</h4>

			<table className={styles['table']}>
				<thead>
					<tr>
						{Array.from({ length: 5 }).map((_, index) => (
							<th key={index}>
								<Skeleton
									className={`${styles['skeleton']} ${styles['small']}`}
								/>
							</th>
						))}
					</tr>
				</thead>

				<tbody>
					{Array.from({ length: 3 }).map((_, index) => (
						<tr key={index}>
							{Array.from({ length: 5 }).map((_, index) => (
								<td key={index}>
									<Skeleton
										className={`${styles['skeleton']} ${styles['small']}`}
									/>
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}
